import { useState, useRef, useMemo, useEffect, useCallback } from "react";
import { SwalSuccess } from "../Share/Swal";
import { CallApiSendZaloOtp, BATCH_SIGNUP_API } from "../Api/Enforcement";
import useReCaptchaV3 from "../Hooks/useReCaptChaV3";
import { CallApiVerifyCaptcha_V3 } from "../Api/Enforcement";
import { signup } from "../Api/SubUrl";
import { useSearchParams } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ShowCustomErrorModal } from "../Components/Atoms/CustomErrorModal";
import { formatDateToIso, parseProfileDateValue } from "../Utils";

const ERROR_MESSAGES = {
  NETWORK_ERROR: "Thực hiện chưa thành công!\n Liên hệ để được hỗ trợ",
  RECAPTCHA_INVALID: "Đã xảy ra lỗi : \nreCAPTCHA không hợp lệ!",
  GENERIC_ERROR: "Thử tải lại trang!\n Hoặc liên hệ fanpage để được hỗ trợ",
  RECAPTCHA_LOADING_ERROR: "Không thể tải reCAPTCHA. Vui lòng tải lại trang.",
  RECAPTCHA_REQUIRED: "Vui lòng hoàn thành xác minh reCAPTCHA.",
};

const SUCCESS_MESSAGES = {
  SUCCESS: "Đăng ký thành công!",
};

const GUARDIAN_AGE = 16;
const MINIMUM_AGE = 18;
const UNDER_16_MESSAGE =
  "Bạn chưa đủ 16 tuổi không thể đăng ký tài khoản.";
const UNDER_18_MESSAGE =
  "Bạn chưa đủ 18 tuổi không thể đăng ký tài khoản";
const VIETNAM_PHONE_REGEX = /^(0|\+84)(3|5|7|8|9)[0-9]{8}$/;
const ACCOUNT_NAME_REGEX = /^[a-z0-9]*$/;
const ACCOUNT_NAME_MIN = 5;
const ACCOUNT_NAME_MAX = 10;
const ACCOUNT_NAME_COUNT = 4;
const ACCOUNT_NAME_LENGTH_MESSAGE =
  "Độ dài từ 5 đến 10 ký tự";
const ACCOUNT_NAME_CHARS_MESSAGE =
  "Sử dụng các ký tự: a-z, 0-9";

const normalizePhoneForApi = (phone = "") => {
  const trimmed = phone.trim();
  if (trimmed.startsWith("+84")) {
    return `0${trimmed.slice(3)}`;
  }
  return trimmed;
};

const getApiErrorMessage = (responseData) => {
  if (typeof responseData === "string") {
    return responseData;
  }

  return responseData?.Message ?? ERROR_MESSAGES.GENERIC_ERROR;
};

const normalizeAccountName = (name = "") => name.trim().toLowerCase();

const getFilledAccountNames = (names = []) =>
  names.map((name) => normalizeAccountName(name)).filter(Boolean);

const mapSignUpPayload = (formData) => ({
  AccountNames: getFilledAccountNames(formData.accountNames),
  FullName: formData.fullName?.trim(),
  DateOfBirth: formatDateToIso(formData.dateOfBirth),
  PhoneNumber: normalizePhoneForApi(formData.phoneNumber),
  OTP: formData.otp?.trim(),
  Password: formData.password,
  RePassword: formData.rePassword,
  SecPassword: formData.secPassword?.trim() ?? "",
});

const normalizeBatchResult = (item) => ({
  accountName: item?.AccountName ?? item?.accountName ?? "",
  success: Boolean(item?.Success ?? item?.success),
  message: item?.Message ?? item?.message ?? ERROR_MESSAGES.GENERIC_ERROR,
});

const collectErrorMessages = (value) => {
  if (!value) {
    return [];
  }
  if (typeof value.message === "string") {
    return [value.message];
  }
  if (Array.isArray(value)) {
    return value.flatMap(collectErrorMessages);
  }
  if (typeof value === "object") {
    return Object.values(value).flatMap(collectErrorMessages);
  }
  return [];
};

const calculateAge = (dateOfBirth) => {
  const birthDate = parseProfileDateValue(dateOfBirth);
  if (!birthDate) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  if (!hasBirthdayPassed) {
    age -= 1;
  }

  return age;
};

export default function useSignUpPage() {
  const isDefaultV3 = process.env.REACT_APP_RECAPTCHA_DEFAULT === "3";
  const [searchParams] = useSearchParams();
  const version = searchParams.get("version");
  const reCaptchaV2 = useRef();

  const [isReCaptchaReady, executeReCaptcha] = useReCaptchaV3();
  const [isLoadReCaptChaV3, setIsLoadReCaptChaV3] = useState(false);
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [idSuccess, setIdSuccess] = useState(null);
  const [idFailed, setIdFailed] = useState([]);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(null);
  const [otpSentToPhone, setOtpSentToPhone] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const recaptchaScriptRef = useRef(null);

  // Memoize validation schema
  const signUpSchemaForm = useMemo(
    () =>
      yup.object().shape({
        accountNames: yup
          .array()
          .of(
            yup
              .string()
              .transform((value) =>
                typeof value === "string" ? value.trim().toLowerCase() : ""
              )
              .optional()
              .test(
                "length-if-present",
                ACCOUNT_NAME_LENGTH_MESSAGE,
                (value) => {
                  if (!value) return true;
                  return (
                    value.length >= ACCOUNT_NAME_MIN &&
                    value.length <= ACCOUNT_NAME_MAX
                  );
                }
              )
              .matches(ACCOUNT_NAME_REGEX, {
                message: ACCOUNT_NAME_CHARS_MESSAGE,
                excludeEmptyString: true,
              })
              .test(
                "unique",
                "Tài khoản đã được sử dụng",
                function (value) {
                  if (!value) return true;
                  const names = Array.isArray(this.parent)
                    ? this.parent
                    : this.from?.[1]?.value?.accountNames;
                  const filled = getFilledAccountNames(
                    Array.isArray(names) ? names : []
                  );
                  return filled.filter((name) => name === value).length <= 1;
                }
              )
          )
          .test(
            "at-least-one",
            "Vui lòng nhập tên tài khoản.",
            (names) => getFilledAccountNames(names).length > 0
          )
          .max(
            ACCOUNT_NAME_COUNT,
            `Chỉ được đăng ký tối đa ${ACCOUNT_NAME_COUNT} tài khoản mỗi lần`
          ),
        fullName: yup
          .string()
          .trim()
          .required("Vui lòng nhập họ và tên.")
          .min(5, "Họ và tên phải có độ dài từ 5 đến 32 ký tự")
          .max(32, "Họ và tên phải có độ dài từ 5 đến 32 ký tự")
          .matches(
            /^[\p{L}\s]+$/u,
            "Họ và tên chỉ được sử dụng chữ cái và khoảng trắng"
          ),
        dateOfBirth: yup
          .date()
          .transform((_, originalValue) => {
            if (!originalValue) return null;
            return parseProfileDateValue(originalValue) ?? new Date(NaN);
          })
          .typeError("Vui lòng nhập ngày sinh hợp lệ.")
          .required("Vui lòng nhập ngày sinh.")
          .max(new Date(), "Ngày sinh không được lớn hơn ngày hiện tại.")
          .test("is-old-enough", UNDER_18_MESSAGE, function (value) {
            const age = calculateAge(value);
            if (age === null) return true;
            if (age < GUARDIAN_AGE) {
              return this.createError({ message: UNDER_16_MESSAGE });
            }
            if (age < MINIMUM_AGE) {
              return this.createError({ message: UNDER_18_MESSAGE });
            }
            return true;
          }),
        phoneNumber: yup
          .string()
          .trim()
          .required("Vui lòng nhập số điện thoại.")
          .matches(
            VIETNAM_PHONE_REGEX,
            "Số điện thoại không hợp lệ."
          ),
        otp: yup.string().trim().required("Vui lòng nhập mã OTP."),
        password: yup
          .string()
          .required("Vui lòng nhập mật khẩu.")
          .min(6, "Mật khẩu phải có độ dài từ 6 đến 32 ký tự")
          .max(32, "Mật khẩu phải có độ dài từ 6 đến 32 ký tự"),
        rePassword: yup
          .string()
          .required("Vui lòng xác nhận lại mật khẩu")
          .test(
            "is-same-password",
            "Mật khẩu xác nhận không trùng khớp",
            function (rePassword) {
              return rePassword === this.parent.password;
            }
          ),
        agreeTerms: yup
          .boolean()
          .oneOf(
            [true],
            "Vui lòng đồng ý với Điều khoản dịch vụ và Chính sách bảo mật."
          )
      }),
    []
  );

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    trigger,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: useMemo(
      () => ({
        accountNames: ["", "", "", ""],
        fullName: "",
        dateOfBirth: "",
        phoneNumber: "",
        otp: "",
        password: "",
        rePassword: "",
        secPassword: "",
        agreeTerms: true
      }),
      []
    ),
    resolver: yupResolver(signUpSchemaForm),
  });

  const dateOfBirth = useWatch({ control, name: "dateOfBirth" });
  const phoneNumber = useWatch({ control, name: "phoneNumber" });
  const age = useMemo(() => calculateAge(dateOfBirth), [dateOfBirth]);
  const isUnder16 = age !== null && age < GUARDIAN_AGE;
  const isUnderAge = age !== null && age < MINIMUM_AGE;

  useEffect(() => {
    setOtpSentToPhone("");
  }, [phoneNumber]);

  useEffect(() => {
    if (!otpSentToPhone) return;
    void trigger("otp");
  }, [otpSentToPhone, trigger]);

  const handleReCaptchaVerify = useCallback(async () => {
    try {
      setIsLoadReCaptChaV3(true);
      const token = await executeReCaptcha("preTest");

      CallApiVerifyCaptcha_V3(token, (res) => {
        setIsLoadReCaptChaV3(false);
        if (res !== "Success") {
          window.location.replace(signup + "?version=2");
        }
      });
    } catch (error) {
      setIsLoadReCaptChaV3(false);
      ShowCustomErrorModal({ message: ERROR_MESSAGES.RECAPTCHA_INVALID });
      window.location.replace(signup + "?version=2");
    }
  }, [executeReCaptcha]);

  useEffect(() => {
    if (version === "2" || !isDefaultV3) {
      return;
    }

    if (isReCaptchaReady && isDefaultV3) {
      handleReCaptchaVerify();
    }
  }, [handleReCaptchaVerify, isDefaultV3, isReCaptchaReady, version]);

  // Improved reCAPTCHA V2 rendering function
  const renderReCaptcha = useCallback(() => {
    if (!window.grecaptcha || !reCaptchaV2.current) {
      return;
    }

    try {
      // Check if already rendered
      const existingRecaptcha =
        reCaptchaV2.current.querySelector(".g-recaptcha");

      if (existingRecaptcha) {
        try {
          window.grecaptcha.reset();
        } catch (resetError) {
          console.error("Failed to reset reCAPTCHA:", resetError);
        }
        return;
      }

      // Render new reCAPTCHA
      window.grecaptcha.render(reCaptchaV2.current, {
        sitekey: process.env.REACT_APP_RECAPTCHA_KEY_V2,
        callback: () => {
          setRecaptchaError(null);
        },
        "expired-callback": () => {
          setRecaptchaError(
            "Mã xác thực reCAPTCHA đã hết hạn. Vui lòng xác minh lại."
          );
        },
        "error-callback": () => {
          setRecaptchaError(
            "Lỗi tải reCAPTCHA. Vui lòng thử lại hoặc tải lại trang."
          );
        },
      });
    } catch (error) {
      // Handle "already rendered" error specifically
      if (error.message && error.message.includes("already been rendered")) {
        try {
          window.grecaptcha.reset();
        } catch (resetError) {
          console.error("Failed to reset reCAPTCHA:", resetError);
        }
      } else {
        setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
      }
    }
  }, []);

  // Improved script loading with proper cleanup
  useEffect(() => {
    if (version === "2" || !isDefaultV3) {
      let scriptLoaded = false;
      let checkInterval = null;

      // Create a more robust cleanup function
      const cleanup = () => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }

        if (recaptchaScriptRef.current) {
          recaptchaScriptRef.current.remove();
          recaptchaScriptRef.current = null;
        }

        // Don't undefine grecaptcha globally, just reset if needed
        if (window.grecaptcha && window.grecaptcha.reset && scriptLoaded) {
          try {
            window.grecaptcha.reset();
          } catch (e) {
            // console.error("Error resetting reCAPTCHA:", e);
          }
        }
      };

      // Load reCAPTCHA script
      const loadRecaptchaScript = () => {
        setRecaptchaLoading(true);
        setRecaptchaError(null);

        // Check if script already exists
        const existingScript = document.querySelector(
          'script[src*="recaptcha/api.js"]'
        );
        if (existingScript) {
          existingScript.remove();
        }

        recaptchaScriptRef.current = document.createElement("script");
        recaptchaScriptRef.current.src =
          "https://www.google.com/recaptcha/api.js?render=explicit";
        recaptchaScriptRef.current.async = true;
        recaptchaScriptRef.current.defer = true;

        recaptchaScriptRef.current.onload = () => {
          scriptLoaded = true;

          // Check for grecaptcha to be fully loaded
          checkInterval = setInterval(() => {
            if (window.grecaptcha && window.grecaptcha.render) {
              clearInterval(checkInterval);
              setRecaptchaLoading(false);
              renderReCaptcha();
            }
          }, 100);

          // Set timeout to avoid infinite checking
          setTimeout(() => {
            if (checkInterval) {
              clearInterval(checkInterval);
              if (!window.grecaptcha || !window.grecaptcha.render) {
                setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
                setRecaptchaLoading(false);
              }
            }
          }, 5000);
        };

        recaptchaScriptRef.current.onerror = () => {
          setRecaptchaLoading(false);
          setRecaptchaError(ERROR_MESSAGES.RECAPTCHA_LOADING_ERROR);
        };

        document.head.appendChild(recaptchaScriptRef.current);
      };

      loadRecaptchaScript();

      // Handle page visibility changes
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && scriptLoaded) {
          // Re-render reCAPTCHA when page becomes visible again
          setTimeout(renderReCaptcha, 300);
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      return () => {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        cleanup();
      };
    }
  }, [version, isDefaultV3, renderReCaptcha]);

  const handleSignUpError = useCallback((res) => {
    if (!res.Status) {
      ShowCustomErrorModal({ message: ERROR_MESSAGES.NETWORK_ERROR });
      return;
    }

    switch (res.Status.status) {
      case 403:
        ShowCustomErrorModal({ message: ERROR_MESSAGES.RECAPTCHA_INVALID });
        break;
      case 400:
        ShowCustomErrorModal({
          message: getApiErrorMessage(res.Status.data),
        });
        break;
      default:
        ShowCustomErrorModal({ message: ERROR_MESSAGES.GENERIC_ERROR });
    }
  }, []);

  const handleSignUp = useCallback(
    async (formData) => {
      if (loading) return;

      try {
        setLoading(true);

        // Get reCAPTCHA token based on version
        let token = { token_v2: "", token_v3: "" };

        if (version === "2" || !isDefaultV3) {
          // Get V2 token
          const v2Token = window.grecaptcha?.getResponse();
          if (!v2Token) {
            ShowCustomErrorModal({
              message: ERROR_MESSAGES.RECAPTCHA_REQUIRED,
            });
            setLoading(false);
            return;
          }
          token.token_v2 = v2Token;
        } else {
          // Get V3 token
          try {
            token.token_v3 = await executeReCaptcha("signUp");
          } catch (error) {
            console.error("Error executing reCAPTCHA v3:", error);
            ShowCustomErrorModal({ message: ERROR_MESSAGES.RECAPTCHA_INVALID });
            setLoading(false);
            return;
          }
        }

        BATCH_SIGNUP_API(token, mapSignUpPayload(formData), (res) => {
          setLoading(false);

          // Reset reCAPTCHA if needed
          if (version === "2" || !isDefaultV3) {
            try {
              window.grecaptcha?.reset();
            } catch (error) {
              console.error("Error resetting reCAPTCHA:", error);
            }
          }

          if (!Array.isArray(res)) {
            handleSignUpError(res);
            return;
          }

          const results = res.map(normalizeBatchResult);
          const succeeded = results
            .filter((item) => item.success)
            .map((item) => item.accountName)
            .filter(Boolean);
          const failed = results.filter((item) => !item.success);

          setIdSuccess(succeeded);
          setIdFailed(failed);

          if (failed.length === 0) {
            SwalSuccess(SUCCESS_MESSAGES.SUCCESS);
            reset();
            return;
          }

          const remainingNames = [
            ...failed.map((item) => item.accountName).filter(Boolean),
          ];
          while (remainingNames.length < ACCOUNT_NAME_COUNT) {
            remainingNames.push("");
          }
          setValue("accountNames", remainingNames.slice(0, ACCOUNT_NAME_COUNT), {
            shouldValidate: true,
          });

          const failedHtml = failed
            .map(
              (item) =>
                `<p>${item.accountName}: ${item.message}</p>`
            )
            .join("");
          ShowCustomErrorModal({
            message: failedHtml,
            title: succeeded.length
              ? "Đăng ký một phần"
              : "Đăng ký không thành công",
          });
        });
      } catch (error) {
        setLoading(false);
        ShowCustomErrorModal({ message: ERROR_MESSAGES.NETWORK_ERROR });
      }
    },
    [
      loading,
      version,
      executeReCaptcha,
      isDefaultV3,
      reset,
      setValue,
      handleSignUpError,
    ]
  );

  const handleTogglePassword = useCallback((isShow) => {
    setIsShowPassword(isShow);
  }, []);

  const handleGetOtp = useCallback(async () => {
    if (isSendingOtp) return;

    const isPhoneValid = await trigger("phoneNumber");
    if (!isPhoneValid) {
      ShowCustomErrorModal({
        message: "<p>Vui lòng nhập số điện thoại hợp lệ</p>",
        title: "Đã xảy ra lỗi",
      });
      return;
    }

    const phone = normalizePhoneForApi(getValues("phoneNumber"));
    setIsSendingOtp(true);

    const token = { token_v2: "", token_v3: "" };
    try {
      if (version === "2" || !isDefaultV3) {
        token.token_v2 = window.grecaptcha?.getResponse();
        if (!token.token_v2) {
          ShowCustomErrorModal({ message: ERROR_MESSAGES.RECAPTCHA_REQUIRED });
          setIsSendingOtp(false);
          return;
        }
      } else {
        token.token_v3 = await executeReCaptcha("sendOtp");
        if (!token.token_v3) throw new Error("Missing reCAPTCHA token");
      }
    } catch (error) {
      setIsSendingOtp(false);
      ShowCustomErrorModal({ message: ERROR_MESSAGES.RECAPTCHA_INVALID });
      return;
    }

    CallApiSendZaloOtp({ Phone: phone }, (res) => {
      setIsSendingOtp(false);

      if (res?.Status) {
        const response = res.Status;
        if (response.status === 400) {
          ShowCustomErrorModal({
            message: `<p>${getApiErrorMessage(response.data)}</p>`,
            title: "Đã xảy ra lỗi",
          });
          return;
        }

        ShowCustomErrorModal({ message: ERROR_MESSAGES.GENERIC_ERROR });
        return;
      }

      if (res?.success) {
        setOtpSentToPhone(phone);
        SwalSuccess(`Không thể gửi mã OTP qua SMS. Vui lòng kiểm tra tin nhắn Zalo để lấy mã xác thực.`);
      } else {
        ShowCustomErrorModal({ message: ERROR_MESSAGES.GENERIC_ERROR });
      }
    });
  }, [executeReCaptcha, getValues, isDefaultV3, isSendingOtp, trigger, version]);

  const handleOtpChange = useCallback(
    (value, fieldOnChange) => {
      fieldOnChange(value);
      void trigger("otp");
    },
    [trigger]
  );

  const handleInvalid = useCallback((errors) => {
    const errorMessages = collectErrorMessages(errors)
      .map((message) => `<p>${message}</p>`)
      .join("");
    ShowCustomErrorModal({ message: errorMessages });
  }, []);

  const submitSignUp = useCallback(
    (event) => handleSubmit(handleSignUp, handleInvalid)(event),
    [handleInvalid, handleSignUp, handleSubmit]
  );

  return {
    version,
    reCaptchaV2,
    isReCaptchaReady,
    isLoadReCaptChaV3,
    isShowPassword,
    control,
    idSuccess,
    idFailed,
    loading,
    recaptchaLoading,
    recaptchaError,
    age,
    isUnder16,
    isUnderAge,
    otpSentToPhone,
    isSendingOtp,
    handleTogglePassword,
    handleGetOtp,
    handleOtpChange,
    handleSubmit: submitSignUp,
    isDefaultV3,
  };
}
